import { motion } from 'framer-motion';
import { Sparkles, ArrowUpRight } from 'lucide-react';

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

export function WorkspaceHero({
  name, actions, quickActions = [],
  eyebrow = 'AI Teaching Workspace',
  description = 'Create curriculum-aligned worksheets, tests, lesson kits and slides in three simple steps.',
}) {
  const firstName = (name || '').trim().split(' ')[0] || 'Teacher';
  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      data-testid="workspace-hero"
      className="relative overflow-hidden rounded-3xl bg-ae-navy text-white px-5 py-6 sm:px-8 sm:py-8 border border-white/5 shadow-soft-lg"
    >
      <div aria-hidden className="absolute inset-0 ae-grid-bg opacity-60 [mask-image:radial-gradient(ellipse_at_top_right,black,transparent_70%)]" />
      <div aria-hidden className="pointer-events-none absolute -top-28 -right-20 h-80 w-80 rounded-full bg-ae-blue/30 blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute -bottom-32 right-1/3 h-72 w-72 rounded-full bg-ae-cyan/15 blur-3xl" />

      <div className="relative flex flex-col xl:flex-row xl:items-start xl:justify-between gap-6">
        <div className="min-w-0 max-w-2xl">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-[0.14em] bg-white/[0.06] text-ae-cyan border border-ae-cyan/25">
            <Sparkles className="h-3.5 w-3.5" /> {eyebrow}
          </span>
          <h1 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-heading font-extrabold tracking-tight" data-testid="workspace-hero-greeting">
            {greeting()}, {firstName}.
          </h1>
          <p className="mt-2 text-sm sm:text-base text-slate-300 leading-relaxed">
            {description}
          </p>
        </div>
        {actions && (
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap" data-testid="workspace-hero-actions">
            {actions}
          </div>
        )}
      </div>

      {quickActions.length > 0 && (
        <div className="relative mt-6 grid grid-cols-1 sm:grid-cols-3 gap-2.5" data-testid="workspace-quick-actions">
          {quickActions.map((qa, i) => {
            const Icon = qa.icon;
            return (
              <motion.button
                key={qa.id}
                type="button"
                onClick={qa.onClick}
                data-testid={`quick-action-${qa.id}`}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + i * 0.06 }}
                className="group flex items-center gap-3 rounded-2xl bg-white/[0.06] hover:bg-white/[0.11] border border-white/10 hover:border-ae-cyan/40 px-4 py-3 text-left transition-[background-color,border-color] duration-200"
              >
                <span className="h-9 w-9 rounded-xl bg-ae-blue/90 text-white flex items-center justify-center shrink-0 transition-transform duration-200 group-hover:scale-110">
                  <Icon className="h-4 w-4" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold">{qa.label}</span>
                  <span className="block text-[11px] text-slate-400 truncate">{qa.hint}</span>
                </span>
                <ArrowUpRight className="h-4 w-4 text-slate-500 group-hover:text-ae-cyan group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-[transform,color] duration-200" />
              </motion.button>
            );
          })}
        </div>
      )}
    </motion.section>
  );
}
